import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, linkedSignal, ResourceRef, Signal, signal, WritableSignal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatList, MatListItem, MatListItemIcon, MatListItemLine, MatListItemMeta, MatListItemTitle } from '@angular/material/list';
import { AlertComponent, FilterChipModel, IconButtonComponent, PageHeaderComponent } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import { ChipFilterGroupComponent } from '@libs/form/components';
import { isDocumentCategory, isTagSlugOfCategory, type DocumentGroupModel, type DocumentModel, type DocumentTagModel, type DocumentTileModel } from '@libs/services/models';
import { FlashMessageService } from '@libs/shared/common';
import { DownloadTrackingService } from '@libs/shared/tracking';
import { LuxonDatePipe } from '@libs/shared/ui';
import { DocumentOverviewSkeletonComponent } from '../document-overview-skeleton';
import { DocumentService } from '../services';

@Component({
  selector: 'ui-document-overview',
  templateUrl: './document-overview.component.html',
  styleUrls: ['./document-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertComponent,
    ChipFilterGroupComponent,
    DocumentOverviewSkeletonComponent,
    IconButtonComponent,
    IconComponent,
    MatList,
    MatListItem,
    MatListItemIcon,
    MatListItemLine,
    MatListItemMeta,
    MatListItemTitle,
    PageHeaderComponent,
  ],
})
export class DocumentOverviewComponent {
  public category: InputSignal<string | undefined> = input<string>();

  public documentsResource: ResourceRef<DocumentModel[] | undefined> = rxResource({
    stream: () => this.documentService.getDocuments(),
  });

  public documents: Signal<DocumentModel[]> = computed(() =>
    this.documentsResource.hasValue() ? this.documentsResource.value() : [],
  );

  // The groups keep the order given by the response and documents with more than one tag are shown in related groups of their tags
  public groups: Signal<DocumentGroupModel[]> = computed(() => {
    const groupsBySlug: Map<string, DocumentGroupModel> = new Map();

    for (const document of this.documents()) {
      const tile: DocumentTileModel = { document, meta: this.documentMetaData(document) };

      for (const tag of document.tags.length > 0 ? document.tags : [this.untaggedTag]) {
        const group: DocumentGroupModel = groupsBySlug.get(tag.slug) ?? { tag, tiles: [] };

        if (!group.tiles.includes(tile)) {
          group.tiles.push(tile);
        }

        groupsBySlug.set(tag.slug, group);
      }
    }

    // Note: the group without a tag is always the last one
    return [...groupsBySlug.values()].sort(
      (a: DocumentGroupModel, b: DocumentGroupModel) => Number(a.tag === this.untaggedTag) - Number(b.tag === this.untaggedTag),
    );
  });

  public filterChips: Signal<FilterChipModel[]> = computed(() =>
    this.groups().map((group: DocumentGroupModel) => ({
      id: group.tag.slug,
      title: group.tag.label,
      count: group.tiles.length,
    })),
  );

  // If no chip is selected then all groups are shown
  public selectedFilterChips: WritableSignal<FilterChipModel[]> = linkedSignal(() => {
    const category: string | undefined = this.category();

    return isDocumentCategory(category)
      ? this.filterChips().filter((chip: FilterChipModel) => isTagSlugOfCategory(String(chip.id), category))
      : [];
  });

  public visibleGroups: Signal<DocumentGroupModel[]> = computed(() => {
    const selectedSlugs: FilterChipModel['id'][] = this.selectedFilterChips().map((chip: FilterChipModel) => chip.id);

    return selectedSlugs.length > 0
      ? this.groups().filter((group: DocumentGroupModel) => selectedSlugs.includes(group.tag.slug))
      : this.groups();
  });

  // Every download has its own loading indicator, so multiple downloads don't clear each other
  public downloadingFileIdentifiers: WritableSignal<ReadonlySet<string>> = signal(new Set());

  private readonly documentService: DocumentService = inject(DocumentService);
  private readonly downloadTracking: DownloadTrackingService = inject(DownloadTrackingService);
  private readonly flashMessage: FlashMessageService = inject(FlashMessageService);
  private readonly luxonDatePipe: LuxonDatePipe = new LuxonDatePipe();

  // Group for documents without a tag
  private readonly untaggedTag: DocumentTagModel = {
    slug: '',
    label: 'Weitere Dokumente',
  };

  public onOpen(document: DocumentModel): void {
    window.open(document.url, document.target, 'noopener');
  }

  public onDownload(document: DocumentModel): void {
    const fileIdentifier: string | undefined = document.fileIdentifier;

    if (!fileIdentifier) {
      return;
    }

    this.downloadTracking.trackDownload(document.name);
    this.toggleDownloading(fileIdentifier, true);

    void this.documentService.downloadDocument(fileIdentifier)
      .catch(() => void this.flashMessage.showMessage('errorDocumentDownload'))
      .finally(() => void this.toggleDownloading(fileIdentifier, false));
  }

  private toggleDownloading(fileIdentifier: string, downloading: boolean): void {
    this.downloadingFileIdentifiers.update((fileIdentifiers: ReadonlySet<string>) => {
      const next: Set<string> = new Set(fileIdentifiers);

      if (downloading) {
        next.add(fileIdentifier);
      } else {
        next.delete(fileIdentifier);
      }

      return next;
    });
  }

  // Create a meta line for documents "description · date · type" (related to order-history)
  private documentMetaData(document: DocumentModel): string {
    return [
      document.description,
      document.validFrom ? this.luxonDatePipe.transform(document.validFrom) : '',
      document.type === 'link' ? 'Link' : 'PDF',
    ].filter(Boolean).join(' · ');
  }
}
